import { errorBox, successBox } from '@core/components/AlertBox';
import { environment } from '@core/environment';
import { buildConMonLink, convertThreeViewportToMapbox } from '@core/logic';
import { ChatBubbleLinesFilled } from '@deepup/icons';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { viewer3dSelectors } from '@viewer3D/redux';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { isupportApiV1 as supportApi } from '@deepup/internal-apis';
import { usePostSupportTicket } from './usePostSupportTicket';

export const IssueReporting = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState('');
  const [descriptionError, setDescriptionError] = useState(false);

  const cameraViewport = useSelector(viewer3dSelectors.cameraViewport);
  const selectedProjectId = useSelector(viewer3dSelectors.selectedProjectId);

  const { isDev } = environment;

  const { postSupportTicket } = usePostSupportTicket();

  const handleOpen = () => {
    setLoading(false);
    setOpen(true);
    setDescription('');
    setDescriptionError(false);
  };

  const handleClose = () => {
    setOpen(false);
    setDescription('');
    setDescriptionError(false);
  };

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value);
    setDescriptionError(event.target.value.trim() === '');
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      setDescriptionError(true);
      return;
    }

    if (!selectedProjectId) return;

    const { zoom, lat, lon } = convertThreeViewportToMapbox(cameraViewport);

    if (!zoom || !lat || !lon) {
      errorBox('Location URL can not be generated. At least one scan must be loaded on the map.');
      return;
    } else {
      const feedbackData: supportApi.CreateSupportTicketRequest = {
        content: description.trim(),
        projectIds: [selectedProjectId],
        locationUrl: buildConMonLink(zoom, lat, lon, isDev),
        recipient: supportApi.SupportTicketRecipient.CUSTOMER,
        zoom,
        orderNumber: '',
        nvtNumber: '',
        location: {
          latitude: lat,
          longitude: lon,
          lat,
          lon,
        },
      };

      setLoading(true);
      const res = await postSupportTicket(feedbackData);

      if (typeof res === 'string') {
        errorBox(res);
        setLoading(false);
        return;
      }

      if (res) {
        handleClose();
        successBox('Issue reported successfully.');
        setLoading(false);
        return;
      }
    }
  };

  return (
    <>
      <Button
        variant="text"
        aria-label="issue-reporting"
        onClick={handleOpen}
        startIcon={<ChatBubbleLinesFilled />}
      >
        <Typography variant="button" fontWeight="bold">
          Feedback
        </Typography>
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="issue-reporting-dialog-title"
        fullWidth
        sx={{ '& .MuiDialog-paperScrollPaper': { overflowX: 'hidden' } }} // prevent horizontal overflow during typing
      >
        <DialogTitle id="issue-reporting-dialog-title">Report Issue</DialogTitle>
        <DialogContent>
          <>
            <TextField
              autoFocus
              disabled={loading}
              label="Description"
              fullWidth
              multiline
              rows={4}
              value={description}
              onChange={handleDescriptionChange}
              error={descriptionError}
              helperText={descriptionError ? 'Description is required' : ''}
              required
              variant="outlined"
              sx={{ mt: 1 }}
            />
            <Box component="div" sx={{ mt: 2, fontSize: '0.75rem', color: 'text.secondary' }}>
              This will include your current viewport and project information.
            </Box>
          </>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
